import { FontSizes, FontWeights } from '@fluentui/react';
import { Button, FluentProvider, makeStyles, Spinner, tokens } from '@fluentui/react-components';
import {
    CheckmarkCircle20Regular,
    DismissRegular,
    ErrorCircle20Regular,
    Info20Regular,
    Warning20Regular,
} from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { usePortal } from '../portal-provider';

export interface BaseToastProps {
    isOpen: boolean;
    title?: React.ReactNode;
    children: React.ReactNode;
    devBoxName?: string;
    autoHideDelay?: number;
    iconName?: string;
    useSpinner?: boolean;
    spinnerAriaLabel?: string;
    backgroundColor: string;
    onDismiss: () => void;
}

const messages = defineMessages({
    dismissButtonAriaLabel: {
        id: 'Toast_DismissButton_AriaLabel',
        defaultMessage: 'Dismiss notification',
        description: 'Aria label for toast dismiss button',
    },
});

// Toast icon name constants
export const ToastIconName = {
    Accept: 'Accept',
    ErrorBadge: 'ErrorBadge',
    Info: 'Info',
    Warning: 'Warning',
} as const;

// Helper function to get the appropriate v2 React icon component
const getIconComponent = (iconName: string, iconStyles: { [key: string]: string }): JSX.Element => {
    switch (iconName) {
        case ToastIconName.Accept: // Success/checkmark icon
            return <CheckmarkCircle20Regular className={iconStyles.success} />;
        case ToastIconName.ErrorBadge: // Error icon
            return <ErrorCircle20Regular className={iconStyles.error} />;
        case ToastIconName.Info: // Info icon
            return <Info20Regular className={iconStyles.info} />;
        case ToastIconName.Warning: // Warning icon
            return <Warning20Regular className={iconStyles.warning} />;
        default:
            return <Info20Regular className={iconStyles.info} />;
    }
};

/**
 * Styles
 */
const useToastContainerStyles = makeStyles({
    root: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        maxWidth: '400px',
        minWidth: '300px',
        pointerEvents: 'auto',
        zIndex: 1001,
    },
});

const useToastCardStyles = makeStyles({
    root: {
        borderRadius: '4px',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.14)',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
});

const useToastHeaderStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        width: '100%',
    },
});

const useToastIconStyles = makeStyles({
    root: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        flexShrink: 0,
        marginTop: '2px',
    },
    success: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        flexShrink: 0,
        marginTop: '2px',
        color: tokens.colorStatusSuccessForeground1,
    },
    error: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        flexShrink: 0,
        marginTop: '2px',
        color: tokens.colorStatusDangerForeground1,
    },
    warning: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        flexShrink: 0,
        marginTop: '2px',
        color: tokens.colorStatusWarningForeground1,
    },
    info: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        flexShrink: 0,
        marginTop: '2px',
        color: tokens.colorPaletteBlueForeground2,
    },
});

const useToastSpinnerStyles = makeStyles({
    root: {
        flexShrink: 0,
        marginTop: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        color: tokens.colorPaletteBlueForeground2,
    },
});

const useToastContentStyles = makeStyles({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
});

const useToastMessageStyles = makeStyles({
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.regular,
        lineHeight: '20px',
        wordWrap: 'break-word',
    },
});

const useToastDismissButtonStyles = makeStyles({
    root: {
        color: tokens.colorNeutralForeground2,
        minWidth: '20px',
        height: '20px',
    },
});

const useToastTitleStyles = makeStyles({
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.semibold,
        lineHeight: '20px',
        wordWrap: 'break-word',
    },
});

const useToastDevBoxNameTextStyles = makeStyles({
    root: {
        fontSize: FontSizes.size12,
        fontWeight: FontWeights.regular,
        lineHeight: '16px',
        color: tokens.colorNeutralForeground2,
    },
});

export const BaseToast: React.FC<BaseToastProps> = React.memo((props) => {
    const {
        isOpen,
        title,
        children,
        devBoxName,
        autoHideDelay = 3000,
        iconName,
        useSpinner = false,
        spinnerAriaLabel,
        backgroundColor,
        onDismiss,
    } = props;

    // Intl hooks
    const { formatMessage } = useIntl(); // Theme hooks
    const theme = useCurrentFluent2Theme();

    // Style hooks
    const toastContainerStyles = useToastContainerStyles();
    const toastCardStyles = useToastCardStyles();
    const toastHeaderStyles = useToastHeaderStyles();
    const toastIconStyles = useToastIconStyles();
    const toastSpinnerStyles = useToastSpinnerStyles();
    const toastContentStyles = useToastContentStyles();
    const toastMessageStyles = useToastMessageStyles();
    const toastDismissButtonStyles = useToastDismissButtonStyles();
    const toastTitleStyles = useToastTitleStyles();
    const toastDevBoxNameTextStyles = useToastDevBoxNameTextStyles();

    // Auto-hide timer
    React.useEffect(() => {
        if (!isOpen || autoHideDelay <= 0) {
            return undefined;
        }

        const timer = setTimeout(() => {
            onDismiss();
        }, autoHideDelay);

        return () => clearTimeout(timer);
    }, [isOpen, autoHideDelay, onDismiss]);

    // Portal rendering
    const toastContent = isOpen ? (
        <FluentProvider theme={theme}>
            <div className={toastContainerStyles.root}>
                <div
                    className={toastCardStyles.root}
                    style={{
                        backgroundColor,
                    }}
                >
                    <div className={toastHeaderStyles.root}>
                        {useSpinner ? (
                            <div className={toastSpinnerStyles.root}>
                                <Spinner aria-label={spinnerAriaLabel} size="tiny" />
                            </div>
                        ) : (
                            getIconComponent(iconName!, toastIconStyles)
                        )}
                        <div className={toastContentStyles.root}>
                            {title && <div className={toastTitleStyles.root}>{title}</div>}
                            <div className={toastMessageStyles.root}>{children}</div>
                            {devBoxName && <div className={toastDevBoxNameTextStyles.root}>{devBoxName}</div>}
                        </div>
                        <Button
                            appearance="transparent"
                            aria-label={formatMessage(messages.dismissButtonAriaLabel)}
                            icon={<DismissRegular />}
                            onClick={onDismiss}
                            size="small"
                            className={toastDismissButtonStyles.root}
                        />
                    </div>
                </div>
            </div>
        </FluentProvider>
    ) : null;

    return usePortal(toastContent);
});
